/**
 * Brand motion — reveal-on-scroll driver.
 *
 * Pairs with oracura-motion.css. The CSS only hides a `.oracura-reveal` element
 * when `.oracura-motion-ready` is present on <html>, and this file is what adds
 * that class. The ordering is deliberate: if this script fails to parse, never
 * loads, or runs in a browser without IntersectionObserver, the class is never
 * added, the hiding rule never matches, and every section renders visible.
 * Content is never gated behind JS.
 *
 * Everything an element needs is a `.oracura-reveal` class — no per-section
 * wiring, and sections added later are picked up automatically because new
 * nodes are observed as they arrive (Shopify's theme editor re-renders a
 * section's markup in place on every settings change).
 */

/**
 * Motion preference, shared by everything on the page that moves on its own.
 *
 * Auto-advancing carousels, the press marquee and the photo wall all keep
 * moving without being asked, which WCAG 2.2.2 requires a way to stop. Pausing
 * on hover and on focus covers a mouse and a keyboard, but neither exists on a
 * touch screen, so there has to be a real control as well — and one shared
 * switch is kinder to the design than a play/pause button bolted onto five
 * separate sections.
 *
 * The state lives in a data attribute on <html>, written before first paint by
 * a small inline script in the layout. Everything reads that attribute rather
 * than importing from here, so nothing depends on this file having loaded yet,
 * and CSS can react to the same flag without any JavaScript at all.
 */
(() => {
  const html = document.documentElement;
  const KEY = 'oracura-motion';

  const paused = () => html.dataset.oracuraMotion === 'paused';

  const set = (next, persist = true) => {
    if (paused() === next) return;
    html.dataset.oracuraMotion = next ? 'paused' : 'auto';
    try {
      // Remembered per visitor. A choice this deliberate should outlast the tab.
      if (persist) localStorage.setItem(KEY, next ? 'paused' : 'auto');
      // A choice made with the control (not the phone's setting changing) also stops or
      // starts the light sweeps, which follow only this (layout/theme.liquid).
      if (persist) html.dataset.oracuraMotionChoice = next ? 'paused' : 'auto';
    } catch (e) {
      // Storage refused; the choice still applies for this page view.
    }
    document.dispatchEvent(new CustomEvent('oracuramotionchange', { detail: { paused: next } }));
  };

  window.OracuraMotion = {
    get paused() {
      return paused();
    },
    set,
    toggle: () => set(!paused()),
    /** Convenience for drivers: run now, and again whenever the choice changes. */
    subscribe(fn) {
      fn(paused());
      const listener = () => fn(paused());
      document.addEventListener('oracuramotionchange', listener);
      return () => document.removeEventListener('oracuramotionchange', listener);
    },
  };

  // A visitor who has not chosen for themselves keeps following the OS setting.
  window.matchMedia?.('(prefers-reduced-motion: reduce)').addEventListener?.('change', (e) => {
    let saved = null;
    try {
      saved = localStorage.getItem(KEY);
    } catch (err) {
      /* unreadable storage means no stored choice */
    }
    if (saved === null) set(e.matches, false);
  });
})();

/**
 * Lazy video, theme-wide.
 *
 * A `[data-video-lazy]` video carries no autoplay attribute and preload="none",
 * so nothing is fetched until it is actually looked at. These files are
 * full-resolution masters — one explore card measured 5.3 MB — and letting the
 * browser fetch them eagerly meant a page could spend most of its weight on a
 * video below the fold.
 *
 * The video-cards carousel has its own copy of this with extra behaviour of its
 * own, so anything inside it is left alone here.
 *
 * Starting a clip is the carousel's recipe, because the plain play() this used
 * to call left Watch and Buy on its poster for good on more devices than not:
 * Safari needs the muted property as well as the attribute, and a source that
 * preload="none" never opened; battery saver (iOS Low Power Mode, Android data
 * saver) and browsers set to block autoplay refuse play() outright, and the
 * refusal was swallowed. A refusal now waits for the next touch or click, which
 * lifts it.
 *
 * A clip inside `[data-video-tap]` also plays and pauses on a tap. That is the
 * way in for a visitor who asked for less motion, or whose browser will not
 * autoplay at all; a clip they start stays theirs until it leaves the screen.
 */
(() => {
  if (!window.IntersectionObserver) return;
  const visible = new Set();
  const observed = new WeakSet();
  // A tap's choice, 'play' or 'pause', until the clip scrolls away.
  const chosen = new WeakMap();
  const preference = matchMedia('(prefers-reduced-motion: reduce)');

  let armed = false;
  const armRetry = () => {
    if (armed) return;
    armed = true;
    const retry = e => {
      armed = false;
      document.removeEventListener('pointerdown', retry);
      document.removeEventListener('touchstart', retry);
      // A touch on a clip is the tap handler's: retrying here as well would
      // start the clip and the click that follows would pause it again.
      if (e.target.closest?.('[data-video-tap]')) return;
      sync();
    };
    document.addEventListener('pointerdown', retry, { once: true, passive: true });
    document.addEventListener('touchstart', retry, { once: true, passive: true });
  };

  const start = video => {
    video.muted = true;
    if (video.readyState === 0) {
      video.preload = 'auto';
      video.load();
    }
    video.play()?.catch(armRetry);
  };

  const sync = () => {
    for (const video of visible) {
      if (!video.isConnected) { visible.delete(video); continue; }
      const choice = chosen.get(video);
      const still = document.hidden || choice === 'pause'
        || (choice !== 'play' && (window.OracuraMotion?.paused || preference.matches));
      if (still) video.pause();
      else start(video);
    }
  };

  // The card shows a play mark while its clip is not moving.
  const mark = video => {
    const shell = video.closest('[data-video-tap]');
    if (shell) {
      shell.classList.toggle('is-playing', !video.paused);
      shell.setAttribute('aria-label', video.paused ? shell.dataset.playLabel : shell.dataset.pauseLabel);
    }
  };

  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) visible.add(entry.target);
      else { visible.delete(entry.target); chosen.delete(entry.target); entry.target.pause(); }
    });
    sync();
  }, { threshold: .5 });
  const scan = scope => scope.querySelectorAll('video[data-video-lazy]').forEach(video => {
    if (video.closest('oracura-video-cards') || observed.has(video)) return;
    observed.add(video); observer.observe(video);
    video.addEventListener('playing', () => mark(video));
    video.addEventListener('pause', () => mark(video));
  });

  const toggle = shell => {
    const video = shell.querySelector('video[data-video-lazy]');
    if (!video) return;
    if (video.paused) {
      chosen.set(video, 'play');
      visible.add(video);
      start(video);
    } else {
      chosen.set(video, 'pause');
      video.pause();
    }
  };
  document.addEventListener('click', e => {
    const shell = e.target.closest?.('[data-video-tap]');
    if (!shell || e.target.closest('a[href]')) return;
    e.preventDefault();
    toggle(shell);
    // The tap is also the gesture a refused autoplay was waiting for.
    sync();
  });
  document.addEventListener('keydown', e => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const shell = e.target.closest?.('[data-video-tap]');
    if (!shell || e.target !== shell) return;
    e.preventDefault();
    toggle(shell);
  });

  scan(document);
  document.addEventListener('shopify:section:load', e => scan(e.target));
  document.addEventListener('shopify:section:unload', e => e.target.querySelectorAll('video').forEach(video => {
    visible.delete(video); observer.unobserve(video); video.pause();
  }));
  document.addEventListener('visibilitychange', sync);
  document.addEventListener('oracuramotionchange', sync);
  preference.addEventListener('change', sync);
})();

(() => {
  const REVEAL_SELECTOR = '.oracura-reveal';
  const ENHANCE_SELECTOR = '.oracura-htu__head, .oracura-htu__card, .oracura-reviews__heading, .oracura-related__title, .oracura-related__card, .oracura-faq__heading, .oracura-pbanner__heading, .oracura-frows__row, .oracura-plp__heading, .oracura-pcard, .oracura-footer__intro, .oracura-journal__card, .oracura-info__offer';
  const READY_CLASS = 'oracura-motion-ready';
  const REVEALED_CLASS = 'is-revealed';

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  // Without IntersectionObserver there is no way to know when an element
  // enters view, so leave everything visible rather than revealing blindly.
  if (typeof IntersectionObserver === 'undefined') return;

  const root = document.documentElement;

  /**
   * Reveals immediately and permanently, used when motion is unwanted. The
   * class still goes on so any styling keyed to the revealed state applies;
   * the CSS media query is what strips the transition.
   */
  const revealNow = (element) => element.classList.add(REVEALED_CLASS);

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add(REVEALED_CLASS);
        // One-shot: a section that has arrived should not re-animate when the
        // visitor scrolls back up past it.
        observer.unobserve(entry.target);
      }
    },
    {
      // Start the transition slightly before the element reaches the viewport
      // edge so it is already settling by the time it is properly in view.
      rootMargin: '0px 0px -8% 0px',
      threshold: 0.01,
    }
  );

  const register = (element) => {
    if (element.dataset.oracuraMotionBound === 'true') return;
    element.dataset.oracuraMotionBound = 'true';

    if (prefersReducedMotion?.matches || window.OracuraMotion?.paused || document.documentElement.classList.contains('shopify-design-mode')) {
      revealNow(element);
      return;
    }

    if (element.getBoundingClientRect().top < innerHeight && element.getBoundingClientRect().bottom > 0) revealNow(element);
    else observer.observe(element);
  };

  const scan = (scope) => {
    if (!(scope instanceof Element) && scope !== document) return;
    scope.querySelectorAll?.(ENHANCE_SELECTOR).forEach(element => element.classList.add('oracura-reveal'));
    const elements = scope.querySelectorAll?.(REVEAL_SELECTOR);
    if (elements) elements.forEach(register);
    if (scope instanceof Element) {
      if (scope.matches(ENHANCE_SELECTOR)) scope.classList.add('oracura-reveal');
      if (scope.matches(REVEAL_SELECTOR)) register(scope);
    }
  };

  const start = () => {
    // Only now does the CSS begin hiding anything.
    root.classList.add(READY_CLASS);
    scan(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // The theme editor swaps section markup in place; newly inserted nodes need
  // observing or they would stay at opacity 0 forever.
  const mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      }
    }
  });

  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('focusin', event => {
    const reveal = event.target.closest?.(REVEAL_SELECTOR);
    if (reveal) { revealNow(reveal); observer.unobserve(reveal); }
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(REVEAL_SELECTOR).forEach(element => observer.unobserve(element));
  });

  document.addEventListener('oracuramotionchange', () => {
    if (window.OracuraMotion?.paused) document.querySelectorAll(REVEAL_SELECTOR).forEach(revealNow);
  });

  // If the visitor turns reduced motion on mid-session, resolve anything still
  // waiting rather than leaving it hidden behind a transition that won't run.
  prefersReducedMotion?.addEventListener?.('change', (event) => {
    if (!event.matches) return;
    document.querySelectorAll(REVEAL_SELECTOR).forEach(revealNow);
  });
})();

/**
 * Back/forward-cache restores.
 *
 * Coming back from a product page, the browser restores this page from cache:
 * the module does not run again, and IntersectionObserver does not re-fire for
 * elements that are already in view. Anything caught mid-reveal stayed at its
 * start transform — which is what pushed the first product card out of the grid
 * and left the gap beside it.
 *
 * On a restore, reveal whatever is still waiting. No animation: the visitor has
 * seen this page already, and the point is that it looks the way they left it.
 */
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  for (const el of document.querySelectorAll('.oracura-reveal:not(.is-revealed)')) {
    el.classList.add('is-revealed');
  }
});
